import { sendEmailVerification, sendPasswordResetEmail as sendFirebasePasswordResetEmail } from 'firebase/auth';
import { auth } from '../firebase';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase';

// Send verification email with custom template
export const sendVerificationEmail = async (userOrEmail, displayName = '') => {
  try {
    let email, userId;
    
    // Handle both user object and email string inputs
    if (typeof userOrEmail === 'string') {
      email = userOrEmail;
      // This will be called from a cloud function for unauthenticated users
      const response = await fetch('https://us-central1-asdflobeta.cloudfunctions.net/sendVerificationEmail', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          displayName: displayName || 'there',
          redirectUrl: `${window.location.origin}/app/dashboard`
        })
      });
      
      if (!response.ok) {
        throw new Error('Failed to send verification email');
      }
      
      return true;
    } else {
      // Handle authenticated user flow
      const user = userOrEmail;
      email = user.email;
      userId = user.uid;
      
      // Configure the action code settings for email verification
      const actionCodeSettings = {
        url: `${window.location.origin}/app/dashboard`,
        handleCodeInApp: true,
      };

      // Send verification email
      await sendEmailVerification(user, actionCodeSettings);
      
      // Log the email verification event
      if (userId) {
        await logEmailEvent(userId, 'verification_email_sent', {
          email,
          timestamp: serverTimestamp()
        });
      }
      
      return true;
    }
  } catch (error) {
    console.error('Error sending verification email:', error);
    throw error;
  }
};

// Send welcome email using the dedicated welcome email endpoint
// Accepts either a user object or { uid, email, displayName }
export const sendWelcomeEmail = async (user, displayName = '') => {
  try {
    let email, uid, name;
    
    // Handle both user object and direct parameter passing
    if (user && typeof user === 'object' && user.email) {
      email = user.email;
      uid = user.uid || user.id;
      name = displayName || user.displayName || user.name || 'there';
    } else if (user && typeof user === 'object' && user.uid) {
      // Handle direct parameter passing with uid
      email = user.email;
      uid = user.uid;
      name = displayName || user.displayName || user.name || 'there';
    } else {
      throw new Error('Invalid user data provided');
    }

    // Call the dedicated welcome email endpoint
    const response = await fetch('https://us-central1-asdflobeta.cloudfunctions.net/sendWelcomeEmail', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify({
        email,
        displayName: name,
        uid,
        redirectUrl: `${window.location.origin}/app/dashboard`
      })
    });

    console.log('Welcome email response status:', response.status);
    
    if (!response.ok) {
      let errorMessage = 'Failed to send welcome email';
      try {
        const errorData = await response.json();
        errorMessage = errorData.error || errorMessage;
      } catch (e) {
        // If we can't parse the error as JSON, use the status text
        errorMessage = await response.text().catch(() => errorMessage);
      }
      throw new Error(errorMessage);
    }
    
    // Log the email event if we have a user ID
    if (uid) {
      try {
        await logEmailEvent(uid, 'welcome_email_sent', {
          email,
          timestamp: serverTimestamp(),
          source: 'admin_triggered'
        });
      } catch (logError) {
        console.error('Error logging email event:', logError);
        // Don't fail the whole operation if logging fails
      }
    }
    
    return true;
  } catch (error) {
    console.error('Error in sendWelcomeEmail:', error);
    throw error; // Re-throw to allow handling in the calling component
  }
};

// Log email events to Firestore
const logEmailEvent = async (userId, eventType, eventData) => {
  if (!userId) return; // Skip if no user ID
  
  try {
    await setDoc(doc(db, 'emailLogs', `${userId}_${Date.now()}`), {
      userId,
      eventType,
      ...eventData,
      timestamp: serverTimestamp()
    });
  } catch (error) {
    console.error('Error logging email event:', error);
    // Don't throw to prevent blocking the main operation
  }
};

// Password reset email
export const sendPasswordResetEmail = async (email) => {
  try {
    const actionCodeSettings = {
      url: `${window.location.origin}/auth?mode=resetPassword`,
      handleCodeInApp: true,
    };
    
    await sendFirebasePasswordResetEmail(auth, email, actionCodeSettings);
    
    await logEmailEvent('system', 'password_reset_email_sent', {
      email,
      timestamp: serverTimestamp()
    });
    
    return true;
  } catch (error) {
    console.error('Error sending password reset email:', error);
    throw error;
  }
};
